// Backend DateTime values come from SQL `datetime2` columns, which EF materialises with
// DateTimeKind.Unspecified. System.Text.Json therefore serialises them WITHOUT a trailing "Z"
// (e.g. "2026-08-25T11:39:16.123"), and `new Date(...)` parses that as *local* time — so a UTC
// timestamp renders as though it were already in the user's zone.
//
// Use these helpers for any timestamp coming from the API. They treat a value with no explicit
// offset as UTC and leave values that already carry one (…Z, +01:00, -05:00) untouched.

const hasExplicitOffset = (s) =>
  s.endsWith('Z') || s.endsWith('z') || /[+-]\d{2}:?\d{2}$/.test(s);

/**
 * Parse an API timestamp into a Date, assuming UTC when no offset is present.
 * @returns {Date|null} null for empty/unparseable input.
 */
export function parseUtcDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  // Date-only values ("2026-08-25") are already parsed as UTC by JS; don't append anything.
  const needsUtcSuffix = trimmed.includes('T') && !hasExplicitOffset(trimmed);
  const parsed = new Date(needsUtcSuffix ? `${trimmed}Z` : trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Format an API timestamp as a local date + time string, or '' if absent/invalid. */
export function formatUtcDateTime(value, options) {
  const d = parseUtcDate(value);
  return d ? d.toLocaleString(undefined, options) : '';
}

/**
 * The same instant as the evidence PDF prints it ("2026-10-07 15:34 UTC"), or '' if absent/invalid.
 * The screen shows local time; pair the two so a reader can see they are one moment.
 */
export function formatUtcStamp(value) {
  const d = parseUtcDate(value);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

/** Format an API timestamp as a local date string, or '' if absent/invalid. */
export function formatUtcDate(value, options) {
  const d = parseUtcDate(value);
  return d ? d.toLocaleDateString(undefined, options) : '';
}
